import { useEffect, useRef, useState } from 'react';

import { Button } from '@components/common/Button';
import { Card, CardTitle } from '@components/common/Card';
import { InfoTooltip } from '@components/common/InfoTooltip';
import {
  useConfirmTenantFundingMethod,
  useRefreshStripeConnection,
  useStartStripeConnection,
  useStartTenantFundingSetupIntent,
  useStripeConnection,
  useTenantFundingPaymentMethods,
  useTenantFundingProfile,
} from '@api/hooks/usePayments';
import { useAuthStore } from '@store/authStore';

const safelyUnmountStripeElement = (element: any) => {
  if (!element) return;
  try {
    element.unmount();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!message.includes('already been destroyed')) {
      console.warn('Failed to unmount Stripe Payment Element', error);
    }
  }
};

export const StripePaymentSettings = () => {
  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElementRef = useRef<any>(null);
  const paymentElementMountRef = useRef<HTMLDivElement | null>(null);

  const { currentUser } = useAuthStore();
  const { data: connection } = useStripeConnection();
  const { data: tenantFunding } = useTenantFundingProfile();
  const { data: tenantFundingPaymentMethods = [] } = useTenantFundingPaymentMethods();
  const startConnection = useStartStripeConnection();
  const refreshConnection = useRefreshStripeConnection();
  const startTenantFundingSetupIntent = useStartTenantFundingSetupIntent();
  const confirmTenantFundingMethod = useConfirmTenantFundingMethod();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [setupError, setSetupError] = useState('');
  const [isConfirmingSetupIntent, setIsConfirmingSetupIntent] = useState(false);
  const [isStripeElementReady, setIsStripeElementReady] = useState(false);
  const [stripeScriptLoaded, setStripeScriptLoaded] = useState<boolean>(() => Boolean((window as any).Stripe));

  const handleConnect = async () => {
    const data = await startConnection.mutateAsync({
      return_path: '/admin/settings?tab=payments',
      force_reconnect: connection?.status === 'ready',
    });

    if (data.onboarding_url) {
      window.location.assign(data.onboarding_url);
    }
  };

  const handleStartTenantFundingSetup = async () => {
    setSetupError('');
    await startTenantFundingSetupIntent.mutateAsync({
      email: currentUser?.email,
      name: currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : undefined,
      payment_method_types: ['us_bank_account', 'card'],
    });
  };

  const handleUseExistingFundingMethod = async () => {
    const normalized = selectedPaymentMethodId.trim();
    if (!normalized) return;
    await confirmTenantFundingMethod.mutateAsync({
      payment_method_id: normalized,
      stripe_customer_id: tenantFunding?.stripe_customer_id || undefined,
    });
  };

  useEffect(() => {
    let cancelled = false;
    if (stripeScriptLoaded) return;

    const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    if (existingScript) {
      setStripeScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      if (!cancelled) setStripeScriptLoaded(true);
    };
    script.onerror = () => {
      if (!cancelled) setSetupError('Failed to load Stripe.js. Check network access and retry.');
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [stripeScriptLoaded]);

  useEffect(() => {
    const clientSecret = tenantFunding?.setup_intent_client_secret;
    const publishableKey = tenantFunding?.publishable_key;
    const mountNode = paymentElementMountRef.current;

    if (!clientSecret || !publishableKey || !stripeScriptLoaded || !mountNode) {
      setIsStripeElementReady(false);
      return;
    }

    let mountedPaymentElement: any = null;

    try {
      const StripeConstructor = (window as any).Stripe;
      if (!StripeConstructor) {
        setSetupError('Stripe.js is not available.');
        return;
      }

      safelyUnmountStripeElement(paymentElementRef.current);
      paymentElementRef.current = null;

      stripeRef.current = StripeConstructor(publishableKey);
      elementsRef.current = stripeRef.current.elements({ clientSecret });
      mountedPaymentElement = elementsRef.current.create('payment');
      mountedPaymentElement.mount(mountNode);
      paymentElementRef.current = mountedPaymentElement;
      setIsStripeElementReady(true);
      setSetupError('');
    } catch (error) {
      console.error('Failed to initialize Stripe Payment Element', error);
      setSetupError('Unable to initialize Stripe payment setup.');
      setIsStripeElementReady(false);
    }

    return () => {
      safelyUnmountStripeElement(mountedPaymentElement);
      if (paymentElementRef.current === mountedPaymentElement) {
        paymentElementRef.current = null;
      }
      setIsStripeElementReady(false);
    };
  }, [tenantFunding?.setup_intent_client_secret, tenantFunding?.publishable_key, stripeScriptLoaded]);

  useEffect(() => {
    if (selectedPaymentMethodId) return;
    const defaultMethod = tenantFundingPaymentMethods.find((method) => method.is_default);
    if (defaultMethod) {
      setSelectedPaymentMethodId(defaultMethod.id);
    }
  }, [selectedPaymentMethodId, tenantFundingPaymentMethods]);

  const handleConfirmSetupIntent = async () => {
    if (!stripeRef.current || !elementsRef.current) {
      setSetupError('Stripe setup form is not ready yet.');
      return;
    }

    setIsConfirmingSetupIntent(true);
    setSetupError('');

    const result = await stripeRef.current.confirmSetup({
      elements: elementsRef.current,
      redirect: 'if_required',
    });

    if (result.error) {
      setSetupError(result.error.message || 'Failed to confirm Stripe setup intent.');
      setIsConfirmingSetupIntent(false);
      return;
    }

    const paymentMethodId = result.setupIntent?.payment_method;
    const normalizedPaymentMethodId =
      typeof paymentMethodId === 'string' ? paymentMethodId : paymentMethodId?.id;

    if (!normalizedPaymentMethodId) {
      setSetupError('Stripe setup completed but no payment method id was returned.');
      setIsConfirmingSetupIntent(false);
      return;
    }

    try {
      await confirmTenantFundingMethod.mutateAsync({
        payment_method_id: normalizedPaymentMethodId,
        stripe_customer_id: tenantFunding?.stripe_customer_id || undefined,
      });
    } finally {
      setIsConfirmingSetupIntent(false);
    }
  };

  return (
    <Card>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CardTitle>Stripe Payments</CardTitle>
          <InfoTooltip>Connect Stripe for this tenant, then onboard vendors before releasing AP payments.</InfoTooltip>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <p className="font-medium text-gray-900">Status: {(connection?.status || 'not_started').replace(/_/g, ' ')}</p>
          <p className="mt-1 text-gray-600">
            {connection?.mode === 'stub'
              ? 'Running in local stub mode until Stripe credentials are configured.'
              : connection?.account_id
              ? `Connected account: ${connection.account_id}`
              : 'No Stripe account connected yet.'}
          </p>
          {connection?.requirements_currently_due?.length ? (
            <p className="mt-2 text-amber-700">
              Requirements due: {connection.requirements_currently_due.join(', ')}
            </p>
          ) : null}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-2">
          <p className="font-medium text-gray-900">
            Tenant Funding Status: {(tenantFunding?.status || 'not_started').replace(/_/g, ' ')}
          </p>
          <p className="text-gray-600">
            {tenantFunding?.default_payment_method_id
              ? `Default funding method: ${tenantFunding.default_payment_method_id}`
              : 'No tenant funding method confirmed yet.'}
          </p>
          {tenantFunding?.requirements_currently_due?.length ? (
            <p className="text-amber-700">
              Requirements due: {tenantFunding.requirements_currently_due.join(', ')}
            </p>
          ) : null}
          {tenantFunding?.setup_intent_client_secret ? (
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 space-y-3">
              <p className="font-medium">Add a new funding method</p>
              <div ref={paymentElementMountRef} className="rounded border border-blue-200 bg-white p-3 min-h-[110px]" />
              {setupError ? <p className="text-red-700">{setupError}</p> : null}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleConfirmSetupIntent}
                  disabled={!isStripeElementReady || isConfirmingSetupIntent || confirmTenantFundingMethod.isPending}
                >
                  {isConfirmingSetupIntent || confirmTenantFundingMethod.isPending ? 'Confirming…' : 'Save Funding Method'}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="pt-1 space-y-2">
            <p className="text-xs font-medium text-gray-700">Use an existing funding method</p>
            {tenantFundingPaymentMethods.length === 0 ? (
              <p className="text-xs text-gray-500">No saved payment methods available yet.</p>
            ) : (
              <div className="space-y-2">
                {tenantFundingPaymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-xs"
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="tenant_funding_method"
                        value={method.id}
                        checked={selectedPaymentMethodId === method.id}
                        onChange={() => setSelectedPaymentMethodId(method.id)}
                      />
                      <span>{method.display_label}</span>
                    </span>
                    {method.is_default ? <span className="text-green-700">Default</span> : null}
                  </label>
                ))}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleUseExistingFundingMethod}
                    disabled={!selectedPaymentMethodId || confirmTenantFundingMethod.isPending}
                  >
                    {confirmTenantFundingMethod.isPending ? 'Saving…' : 'Use Selected Method'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={() => refreshConnection.mutateAsync()}>
            Refresh
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleStartTenantFundingSetup}
            disabled={startTenantFundingSetupIntent.isPending}
          >
            {startTenantFundingSetupIntent.isPending ? 'Starting…' : 'Start Tenant Funding'}
          </Button>
          <Button type="button" onClick={handleConnect} disabled={startConnection.isPending}>
            {startConnection.isPending ? 'Starting…' : connection?.status === 'ready' ? 'Reconnect Stripe' : 'Connect Stripe'}
          </Button>
        </div>
      </div>
    </Card>
  );
};
